import { DocumentNode, gql } from '@apollo/client';
import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { dinero } from 'dinero.js';
import NextLink from 'next/link';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import {
  MembershipCTAType,
  MembershipStatus,
} from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Emoji } from '@/imports/ui/chakra/components/Emoji';

import { MembershipCtaCard_MembershipDataFragment } from './graphql-types/MembershipCtaCard_MembershipDataFragment';

export const MembershipCtaCard: React.FC<{
  membershipData: MembershipCtaCard_MembershipDataFragment;
  userFirstName?: string;
  organizationName?: string;
  shouldBeMemberView: boolean;
}> & { membershipDataFragment: DocumentNode } = ({
  membershipData,
  userFirstName,
  organizationName,
  shouldBeMemberView,
}) => {
  const name = organizationName || userFirstName;
  const current = useCurrent();
  const acronym = current.tenant?.acronym;
  const displayName = current.tenant?.displayName;
  const isUpcomingTerm = dayjs(
    membershipData.callToAction?.relevantMembershipTerm.startDate
  ).isAfter(dayjs());

  const isPastDue = dayjs(membershipData.callToAction?.dueDate).isBefore(
    dayjs()
  );

  const differenceInDays = Math.abs(
    dayjs(membershipData.callToAction?.dueDate).diff(dayjs(), 'day')
  );
  const getPastDueCTAText = () => {
    return differenceInDays === 0
      ? 'Payment is Past Due.'
      : `Payment is ${differenceInDays} Day${
          differenceInDays === 1 ? '' : 's'
        } Past Due.`;
  };

  let ctaData = {
    emojiSymbol: '',
    primaryCopy: '',
    secondaryCopy: '',
    buttonText: '',
    linkHref: '',
    isLinkExternal: false,
    showPrice: false,
    showInvoice: false,
  };

  if (shouldBeMemberView) {
    // Specs from table C: https://tinyurl.com/ytcz467b
    switch (membershipData.membershipStatus) {
      case MembershipStatus.MEMBER:
        ctaData = {
          emojiSymbol: '🏅',
          primaryCopy: 'Thanks for Being a Valued Member!',
          secondaryCopy: `As a member of ${
            acronym || displayName
          }, you have access to so much more!`,
          buttonText: '',
          linkHref: '',
          isLinkExternal: false,
          showPrice: false,
          showInvoice: false,
        };

        break;

      case MembershipStatus.NOT_A_MEMBER:
      case MembershipStatus.LAPSED:
        ctaData = {
          emojiSymbol: '☝️',
          primaryCopy: 'Unlock Additional Benefits',
          secondaryCopy: `Members of ${
            acronym || displayName
          } have access to so much more! Join today.`,
          buttonText: 'Explore Memberships',
          linkHref: '/memberships',
          isLinkExternal: false,
          showPrice: false,
          showInvoice: false,
        };

        break;
      default:
        break;
    }
  }

  switch (membershipData.callToAction?.type) {
    // See specs in both table A and B: https://tinyurl.com/mws76yb2
    case MembershipCTAType.ACH_VERIFICATION_NEEDED:
      if (
        membershipData.callToAction.relevantMembershipTerm.paymentOrder
          ?.inFlightStripePaymentTransaction?.paymentMethodObject
          ?.__typename === 'PaymentMethod_UsBankAccount' &&
        membershipData.callToAction.relevantMembershipTerm.paymentOrder
          .inFlightStripePaymentTransaction.paymentMethodObject.verificationUrl
      ) {
        ctaData = {
          emojiSymbol: '🧐',
          primaryCopy: shouldBeMemberView
            ? isUpcomingTerm
              ? `Please Verify your ACH Payment Method within ${differenceInDays} Day${
                  differenceInDays === 1 ? '' : 's'
                }`
              : 'Verification Needed for ACH Payment'
            : 'Verification Needed for ACH Payment',
          secondaryCopy: shouldBeMemberView
            ? isUpcomingTerm
              ? `Please verify the ACH account in ${differenceInDays} day${
                  differenceInDays === 1 ? '' : 's'
                }.`
              : `Please verify your ACH payment method within ${differenceInDays} day${
                  differenceInDays === 1 ? '' : 's'
                }`
            : `Please verify ${name}’s ACH payment method within ${differenceInDays} day${
                differenceInDays === 1 ? '' : 's'
              }.`,
          buttonText: 'Verify',
          linkHref:
            membershipData.callToAction.relevantMembershipTerm.paymentOrder
              .inFlightStripePaymentTransaction.paymentMethodObject
              .verificationUrl,
          isLinkExternal: true,
          showPrice: true,
          showInvoice: false,
        };
      }

      break;
    case MembershipCTAType.UNPAID_INVOICE:
      ctaData = {
        emojiSymbol: isPastDue ? '🚨' : '☝️',
        primaryCopy: `Your Invoice For ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} Needs Payment`,
        secondaryCopy: isPastDue
          ? getPastDueCTAText()
          : `Payment is due in ${differenceInDays} day${
              differenceInDays === 1 ? '' : 's'
            }.`,
        buttonText: shouldBeMemberView ? 'Pay now' : 'Receive payment',
        linkHref: shouldBeMemberView
          ? `/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/pay`
          : `/admin/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/receive-payment`,
        isLinkExternal: false,
        showPrice: true,
        showInvoice: true,
      };

      break;
    case MembershipCTAType.PAYMENT_FAILED:
      ctaData = {
        emojiSymbol: '🚨',
        primaryCopy: shouldBeMemberView
          ? `Your Payment Failed for ${membershipData.callToAction.relevantMembershipTerm.membershipType.name}`
          : `${name}’s Payment Failed for ${membershipData.callToAction.relevantMembershipTerm.membershipType.name}`,
        secondaryCopy: isPastDue
          ? getPastDueCTAText()
          : `Payment is due in ${differenceInDays} day${
              differenceInDays === 1 ? '' : 's'
            }.`,
        buttonText: shouldBeMemberView
          ? isUpcomingTerm
            ? 'Pay Now'
            : 'Try Again'
          : 'Receive Payment',
        linkHref: shouldBeMemberView
          ? `/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/pay`
          : `/admin/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/receive-payment`,
        isLinkExternal: false,
        showPrice: true,
        showInvoice: false,
      };

      break;
    case MembershipCTAType.PAYMENT_NEEDED:
      ctaData = {
        emojiSymbol: '☝️',
        primaryCopy: shouldBeMemberView
          ? `Your ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} Needs Payment`
          : `${name}’s ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} Needs Payment`,
        secondaryCopy: `Payment is due in ${differenceInDays} day${
          differenceInDays === 1 ? '' : 's'
        }.`,
        buttonText: shouldBeMemberView ? 'Pay Now' : 'Receive Payment',
        linkHref: shouldBeMemberView
          ? `/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/pay`
          : `/admin/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/receive-payment`,
        isLinkExternal: false,
        showPrice: true,
        showInvoice: false,
      };

      break;
    case MembershipCTAType.PAST_DUE:
      ctaData = {
        emojiSymbol: '🚨',
        primaryCopy: shouldBeMemberView
          ? `Your ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} is Past Due`
          : `${name}’s ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} is Past Due`,
        secondaryCopy: getPastDueCTAText(),
        buttonText: shouldBeMemberView ? 'Pay Now' : 'Receive Payment',
        linkHref: shouldBeMemberView
          ? `/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/pay`
          : `/admin/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/receive-payment`,
        isLinkExternal: false,
        showPrice: true,
        showInvoice: false,
      };

      break;
    case MembershipCTAType.UP_FOR_RENEWAL:
      ctaData = {
        emojiSymbol: '☝️',
        primaryCopy: shouldBeMemberView
          ? `Your ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} is Up for Renewal`
          : `${name}’s ${membershipData.callToAction.relevantMembershipTerm.membershipType.name} Needs Payment`,
        secondaryCopy: `Payment is due in ${differenceInDays} day${
          differenceInDays === 1 ? '' : 's'
        }.`,
        buttonText: shouldBeMemberView ? 'Pay Now' : 'Receive Payment',
        linkHref: shouldBeMemberView
          ? `/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/pay`
          : `/admin/membership-orders/${membershipData.callToAction.relevantMembershipTerm.paymentOrder?._id}/receive-payment`,
        isLinkExternal: false,
        showPrice: true,
        showInvoice: false,
      };

      break;
    default:
      break;
  }

  const {
    emojiSymbol,
    primaryCopy,
    secondaryCopy,
    buttonText,
    linkHref,
    isLinkExternal,
    showPrice,
    showInvoice,
  } = ctaData;

  if (!primaryCopy) {
    return null;
  }

  return (
    <Card padding="6">
      {emojiSymbol && <Emoji symbol={emojiSymbol} fontSize="3xl" />}

      <Stack spacing="2" justifyContent="space-between">
        {primaryCopy && <Text textStyle="xlBodyStrong">{primaryCopy}</Text>}

        <Stack spacing="0">
          {showPrice &&
            membershipData.callToAction?.relevantMembershipTerm.paymentOrder
              ?.paymentDue && (
              <CurrencyDisplay
                textStyle="xlBodyStrong"
                value={dinero(
                  membershipData.callToAction.relevantMembershipTerm
                    .paymentOrder.paymentDue
                )}
              />
            )}

          <Stack spacing="4">
            {secondaryCopy && <Text>{secondaryCopy}</Text>}

            {(buttonText || showInvoice) && (
              <Stack>
                {buttonText && isLinkExternal ? (
                  <Button
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={linkHref}
                  >
                    {buttonText}
                  </Button>
                ) : (
                  buttonText && (
                    <NextLink href={linkHref}>
                      <Button>{buttonText}</Button>
                    </NextLink>
                  )
                )}

                {showInvoice &&
                  membershipData.callToAction?.relevantMembershipTerm
                    .paymentOrder?.invoice?.pdfUrl && (
                    <Button
                      as="a"
                      href={
                        membershipData.callToAction.relevantMembershipTerm
                          .paymentOrder.invoice.pdfUrl
                      }
                      variant="outline"
                      colorScheme="gray"
                    >
                      View Invoice
                    </Button>
                  )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

MembershipCtaCard.membershipDataFragment = gql`
  fragment MembershipCtaCard_MembershipDataFragment on MembershipData {
    membershipStatus
    callToAction {
      type
      dueDate
      relevantMembershipTerm {
        _id
        startDate
        membershipType {
          _id
          name
        }
        paymentOrder {
          _id
          paymentDue
          paymentInterval
          inFlightStripePaymentTransaction {
            _id
            paymentMethodObject {
              _id
              ... on PaymentMethod_UsBankAccount {
                verificationUrl
              }
            }
          }
          invoice {
            pdfUrl
          }
        }
      }
    }
  }
`;
